import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { api } from '../lib/api.ts';
import { activeMocks } from '../shell/mocks.ts';
import { AudioPlayer } from '../voice/player.ts';
import { type AudioSource, MicAudioSource, PcmFixtureAudioSource } from '../voice/sources.ts';
import { GeminiLiveTransport, parseFixture, ScriptedTransport } from '../voice/transport.ts';
import { Conversation } from './conversation.ts';

class SilentSource implements AudioSource {
  readonly kind = 'fixture' as const;
  async start() {}
  stop() {}
}

/**
 * Test hooks via URL (harmless in production: they only change this browser's own session):
 *   ?transport=scripted:<fixture>   replay e2e/fixtures/sessions/<fixture>.jsonl (served by Playwright)
 *   ?audio=fixture:<a>,<b>          stream e2e/fixtures/audio/<a>.pcm after each model turn instead of the mic
 *   ?audio=none                     no audio input (text only)
 *   ?audio=mic                      the real mic even with a replay (e2e: the mic-permission paths)
 *   ?debug=1                        exposes window.__fuse (the Conversation) for live tests, e.g. forced reconnects
 */
export function readTestParams(search = window.location.search) {
  const q = new URLSearchParams(search);
  const transport = q.get('transport') ?? '';
  const audio = q.get('audio') ?? '';
  return {
    scriptedFixture: transport.startsWith('scripted:') ? transport.slice('scripted:'.length) : null,
    audioFixtures: audio.startsWith('fixture:') ? audio.slice('fixture:'.length).split(',').filter(Boolean) : null,
    noAudio: audio === 'none',
    forceMic: audio === 'mic',
    debug: q.get('debug') === '1',
  };
}

/**
 * Where a call's transport and audio come from, read when the call starts: URL test params (e2e fixtures from
 * Playwright), else an admin's mocks (fixtures from the server), else Gemini and the mic.
 */
function callSources(params: ReturnType<typeof readTestParams>) {
  const mocks = activeMocks();
  const replay = params.scriptedFixture
    ? `/__fixtures/sessions/${params.scriptedFixture}.jsonl`
    : mocks?.replay
      ? `/api/admin/fixtures/sessions/${mocks.replay}.jsonl`
      : null;
  const audio = params.audioFixtures
    ? { names: params.audioFixtures, base: '/__fixtures/audio', everyTask: false }
    : mocks?.audio
      ? { names: [`task:${mocks.audio}`], base: '/api/admin/fixtures/audio', everyTask: true }
      : null;
  return { replay, audio };
}

export function useConversation() {
  const params = useMemo(() => readTestParams(), []);
  const convo = useMemo(() => {
    // One set of sources per call: taken when the call starts (the conversation reads transportKind first), then used
    // by the transport, the audio source and any reconnect, so a mock changed mid-call can't mix two.
    let sources: ReturnType<typeof callSources> = { replay: null, audio: null };
    return new Conversation({
      api,
      player: new AudioPlayer(),
      get transportKind() {
        sources = callSources(params);
        return sources.replay ? ('scripted' as const) : ('gemini' as const);
      },
      async makeTransport(session) {
        const { replay } = sources;
        if (replay) {
          const res = await fetch(replay);
          if (!res.ok) throw new Error(`replay ${replay} not found`);
          return new ScriptedTransport(parseFixture(await res.text()));
        }
        if (!session.token) throw new Error('server returned no live token');
        return new GeminiLiveTransport(session.token, session.model);
      },
      makeSource() {
        const { replay, audio } = sources;
        if (audio) return new PcmFixtureAudioSource(audio.names, audio.base, { everyTask: audio.everyTask });
        if (params.forceMic) return new MicAudioSource();
        if (params.noAudio || replay) return new SilentSource();
        return new MicAudioSource();
      },
    });
  }, [params]);
  // In an effect, not in useMemo: StrictMode runs memo factories twice in dev and discards one instance.
  useEffect(() => {
    if (params.debug) (window as unknown as { __fuse?: Conversation }).__fuse = convo;
  }, [params, convo]);
  const snap = useSyncExternalStore(convo.subscribe, convo.getSnapshot);
  return { convo, snap };
}
